import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../database/firebase";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Plus,
  Check,
  Activity,
  Droplets,
  Utensils,
  Moon,
  TrendingUp,
  Calendar,
  Award,
  Flame,
  Target,
  ChevronRight,
} from "lucide-react";

const Tracker = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("daily");
  const [todayData, setTodayData] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.uid) {
      loadTrackerData();
    }
  }, [user]);

  const loadTrackerData = async () => {
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const today = new Date().toISOString().split("T")[0];
        
        // Load today's data
        const todayRecord = userData.healthInfo?.dailyTracking?.records?.find(
          (r) => r.date === today
        );
        setTodayData(todayRecord || { date: today, waterGlasses: 0, meals: [], steps: 0, sleep: 0 });
        
        // Load monthly data (current month)
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const monthRecords = (userData.healthInfo?.dailyTracking?.records || []).filter(r => {
          const recordDate = new Date(r.date);
          return recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear;
        });
        setMonthlyData(monthRecords);
        
        // Load goals
        setGoals(userData.healthInfo?.goals || {
          water: { daily: 8 }, // glasses
          meals: { daily: 3 },
          steps: { daily: 10000 },
          sleep: { daily: 8 }
        });
      }
    } catch (error) {
      console.error("Error loading tracker data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogWater = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const existingRecords = userData.healthInfo?.dailyTracking?.records || [];
      const todayIndex = existingRecords.findIndex((r) => r.date === today);

      let updatedRecords;
      if (todayIndex >= 0) {
        updatedRecords = [...existingRecords];
        const currentGlasses = updatedRecords[todayIndex].waterGlasses || 0;
        updatedRecords[todayIndex] = {
          ...updatedRecords[todayIndex],
          waterGlasses: currentGlasses + 1,
          water: ((currentGlasses + 1) * 0.25).toFixed(2), // 250ml per glass
          updatedAt: new Date().toISOString(),
        };
      } else {
        updatedRecords = [
          ...existingRecords,
          {
            date: today,
            waterGlasses: 1,
            water: 0.25,
            meals: [],
            steps: 0,
            sleep: 0,
            createdAt: new Date().toISOString(),
          },
        ];
      }

      await updateDoc(userRef, {
        "healthInfo.dailyTracking.records": updatedRecords,
        updatedAt: new Date().toISOString(),
      });

      toast.success("Water logged!");
      loadTrackerData();
    } catch (error) {
      console.error("Error logging water:", error);
      toast.error("Failed to log water");
    }
  };

  const handleLogMeal = async (mealType) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const existingRecords = userData.healthInfo?.dailyTracking?.records || [];
      const todayIndex = existingRecords.findIndex((r) => r.date === today);

      let updatedRecords;
      let mealExists = false; // Declare outside to use in toast message
      
      if (todayIndex >= 0) {
        updatedRecords = [...existingRecords];
        const currentMeals = updatedRecords[todayIndex].meals || [];
        
        // Toggle meal
        mealExists = currentMeals.some(m => m.type === mealType);
        const newMeals = mealExists 
          ? currentMeals.filter(m => m.type !== mealType)
          : [...currentMeals, { type: mealType, time: new Date().toISOString() }];
        
        updatedRecords[todayIndex] = {
          ...updatedRecords[todayIndex],
          meals: newMeals,
          updatedAt: new Date().toISOString(),
        };
      } else {
        updatedRecords = [
          ...existingRecords,
          {
            date: today,
            waterGlasses: 0,
            water: 0,
            meals: [{ type: mealType, time: new Date().toISOString() }],
            steps: 0,
            sleep: 0,
            createdAt: new Date().toISOString(),
          },
        ];
      }

      await updateDoc(userRef, {
        "healthInfo.dailyTracking.records": updatedRecords,
        updatedAt: new Date().toISOString(),
      });

      toast.success(`${mealType} ${mealExists ? 'removed' : 'logged'}!`);
      loadTrackerData();
    } catch (error) {
      console.error("Error logging meal:", error);
      toast.error("Failed to log meal");
    }
  };

  const handleUpdateSteps = async (steps) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const existingRecords = userData.healthInfo?.dailyTracking?.records || [];
      const todayIndex = existingRecords.findIndex((r) => r.date === today);

      let updatedRecords;
      if (todayIndex >= 0) {
        updatedRecords = [...existingRecords];
        updatedRecords[todayIndex] = {
          ...updatedRecords[todayIndex],
          steps: parseInt(steps),
          updatedAt: new Date().toISOString(),
        };
      } else {
        updatedRecords = [
          ...existingRecords,
          {
            date: today,
            waterGlasses: 0,
            water: 0,
            meals: [],
            steps: parseInt(steps),
            sleep: 0,
            createdAt: new Date().toISOString(),
          },
        ];
      }

      await updateDoc(userRef, {
        "healthInfo.dailyTracking.records": updatedRecords,
        updatedAt: new Date().toISOString(),
      });

      toast.success("Steps updated!");
      loadTrackerData();
    } catch (error) {
      console.error("Error updating steps:", error);
      toast.error("Failed to update steps");
    }
  };

  const handleUpdateSleep = async (hours) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const existingRecords = userData.healthInfo?.dailyTracking?.records || [];
      const todayIndex = existingRecords.findIndex((r) => r.date === today);

      let updatedRecords;
      if (todayIndex >= 0) {
        updatedRecords = [...existingRecords];
        updatedRecords[todayIndex] = {
          ...updatedRecords[todayIndex],
          sleep: parseFloat(hours),
          updatedAt: new Date().toISOString(),
        };
      } else {
        updatedRecords = [
          ...existingRecords,
          {
            date: today,
            waterGlasses: 0,
            water: 0,
            meals: [],
            steps: 0,
            sleep: parseFloat(hours),
            createdAt: new Date().toISOString(),
          },
        ];
      }

      await updateDoc(userRef, {
        "healthInfo.dailyTracking.records": updatedRecords,
        updatedAt: new Date().toISOString(),
      });

      toast.success("Sleep updated!");
      loadTrackerData();
    } catch (error) {
      console.error("Error updating sleep:", error);
      toast.error("Failed to update sleep");
    }
  };

  // Calculate monthly statistics
  const getMonthlyStats = () => {
    if (monthlyData.length === 0) return null;

    const totalWater = monthlyData.reduce((sum, day) => sum + (day.waterGlasses || 0), 0);
    const totalMeals = monthlyData.reduce((sum, day) => sum + (day.meals?.length || 0), 0);
    const totalSteps = monthlyData.reduce((sum, day) => sum + (day.steps || 0), 0);
    const totalSleep = monthlyData.reduce((sum, day) => sum + (day.sleep || 0), 0);

    const daysTracked = monthlyData.length;
    const avgWater = (totalWater / daysTracked).toFixed(1);
    const avgMeals = (totalMeals / daysTracked).toFixed(1);
    const avgSteps = Math.round(totalSteps / daysTracked);
    const avgSleep = (totalSleep / daysTracked).toFixed(1);

    // Calculate streak
    const sortedDays = [...monthlyData].sort((a, b) => new Date(b.date) - new Date(a.date));
    let streak = 0;
    const today = new Date();
    for (let i = 0; i < sortedDays.length; i++) {
      const dayDate = new Date(sortedDays[i].date);
      const expectedDate = new Date(today);
      expectedDate.setDate(today.getDate() - i);
      
      if (dayDate.toDateString() === expectedDate.toDateString()) {
        streak++;
      } else {
        break;
      }
    }

    return {
      daysTracked,
      avgWater,
      avgMeals,
      avgSteps,
      avgSleep,
      totalWater,
      totalSteps,
      streak
    };
  };

  const monthlyStats = getMonthlyStats();

  const DailyGoalCard = ({ icon: Icon, title, current, target, unit, color, onAdd, onUpdate, type }) => {
    const progress = target > 0 ? Math.min((current / target) * 100, 100) : 0;
    const isMeal = type === "meal";
    const meals = isMeal ? (todayData?.meals || []) : [];

    return (
      <div className="bg-gray-50 rounded-3xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: color + "20" }}>
              <Icon className="w-6 h-6" style={{ color }} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-500">
                {current} / {target} {unit}
              </p>
            </div>
          </div>
          {onAdd && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onAdd}
              className="w-10 h-10 rounded-full bg-[#CDFF00] flex items-center justify-center hover:bg-[#b8e600] transition-colors"
            >
              <Plus className="w-5 h-5 text-black" />
            </motion.button>
          )}
        </div>

        {isMeal ? (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {["breakfast", "lunch", "dinner"].map((meal) => {
              const isLogged = meals.some(m => m.type === meal);
              return (
                <button
                  key={meal}
                  onClick={() => handleLogMeal(meal)}
                  className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                    isLogged
                      ? "bg-[#CDFF00] text-black"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {isLogged && <Check className="w-4 h-4 inline mr-1" />}
                  {meal.charAt(0).toUpperCase() + meal.slice(1)}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="h-3 bg-white rounded-full overflow-hidden mb-3">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full rounded-full"
              style={{ backgroundColor: color }}
            />
          </div>
        )}

        {progress >= 100 && !isMeal && (
          <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
            <Check className="w-4 h-4" />
            Goal achieved!
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CDFF00] mx-auto mb-4"></div>
          <p className="text-gray-500">Loading tracker...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="px-6 py-6 bg-gradient-to-br from-[#CDFF00]/20 to-white border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full hover:bg-white/50 flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-black">Wellness Tracker</h1>
              <p className="text-sm text-gray-600">
                {new Date().toLocaleDateString("en-US", { 
                  weekday: "long", 
                  month: "long", 
                  day: "numeric" 
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("daily")}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
              activeTab === "daily"
                ? "bg-[#CDFF00] text-black"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setActiveTab("monthly")}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
              activeTab === "monthly"
                ? "bg-[#CDFF00] text-black"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            This Month
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {activeTab === "daily" && (
          <div className="space-y-4">
            <DailyGoalCard
              icon={Droplets}
              title="Water Intake"
              current={todayData?.waterGlasses || 0}
              target={goals?.water?.daily || 8}
              unit="glasses"
              color="#06b6d4"
              onAdd={handleLogWater}
            />

            <DailyGoalCard
              icon={Utensils}
              title="Meals"
              current={todayData?.meals?.length || 0}
              target={goals?.meals?.daily || 3}
              unit="meals"
              color="#f97316"
              type="meal"
            />

            <DailyGoalCard
              icon={Activity}
              title="Activity"
              current={todayData?.steps || 0}
              target={goals?.steps?.daily || 10000}
              unit="steps"
              color="#8b5cf6"
            />

            <DailyGoalCard
              icon={Moon}
              title="Sleep"
              current={todayData?.sleep || 0}
              target={goals?.sleep?.daily || 8}
              unit="hours"
              color="#6366f1"
            />

            {/* Quick Update Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-3xl p-6 mt-6">
              <h3 className="font-bold text-gray-900 mb-4">Quick Update</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Steps Today
                  </label>
                  <input
                    type="number"
                    value={todayData?.steps || 0}
                    onChange={(e) => handleUpdateSteps(e.target.value)}
                    className="w-full px-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-[#CDFF00] outline-none"
                    placeholder="Enter steps"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Sleep Hours
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={todayData?.sleep || 0}
                    onChange={(e) => handleUpdateSleep(e.target.value)}
                    className="w-full px-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-[#CDFF00] outline-none"
                    placeholder="Enter hours"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "monthly" && monthlyStats && (
          <div className="space-y-6">
            {/* Monthly Stats Cards */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Monthly Overview</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-5">
                  <Calendar className="w-8 h-8 text-cyan-600 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">Days Tracked</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats.daysTracked}</p>
                  <p className="text-xs text-gray-500 mt-1">out of {new Date().getDate()} days</p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-5">
                  <Flame className="w-8 h-8 text-orange-600 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">Streak</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats.streak}</p>
                  <p className="text-xs text-gray-500 mt-1">days in a row</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-5">
                  <Droplets className="w-8 h-8 text-cyan-600 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">Avg Water</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats.avgWater}</p>
                  <p className="text-xs text-gray-500 mt-1">glasses/day</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-5">
                  <Activity className="w-8 h-8 text-purple-600 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">Avg Steps</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats.avgSteps.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">steps/day</p>
                </div>
              </div>
            </div>

            {/* Detailed Stats */}
            <div className="bg-gray-50 rounded-3xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">Detailed Statistics</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Total Water</span>
                  <span className="font-semibold text-gray-900">{monthlyStats.totalWater} glasses</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Avg Meals</span>
                  <span className="font-semibold text-gray-900">{monthlyStats.avgMeals} meals/day</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Total Steps</span>
                  <span className="font-semibold text-gray-900">{monthlyStats.totalSteps.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Avg Sleep</span>
                  <span className="font-semibold text-gray-900">{monthlyStats.avgSleep} hours/night</span>
                </div>
              </div>
            </div>

            {/* Achievement */}
            {monthlyStats.streak >= 7 && (
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="w-8 h-8" />
                  <h4 className="font-bold text-lg">Amazing Streak!</h4>
                </div>
                <p className="text-sm opacity-90">
                  You've tracked your wellness for {monthlyStats.streak} days in a row. Keep it up!
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "monthly" && !monthlyStats && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">No Data Yet</h3>
            <p className="text-gray-500 mb-4">
              Start tracking your daily goals to see monthly statistics
            </p>
            <button
              onClick={() => setActiveTab("daily")}
              className="px-6 py-3 bg-[#CDFF00] text-black rounded-xl font-semibold hover:bg-[#b8e600] transition-colors"
            >
              Start Tracking
            </button>
          </div>
        )}
      </div>

      {/* Bottom Indicator */}
      <div className="fixed bottom-0 left-0 right-0 h-8 bg-white flex items-center justify-center">
        <div className="w-32 h-1 bg-black rounded-full"></div>
      </div>
    </div>
  );
};

export default Tracker;
